import React, { useState } from 'react';
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import '../Style/Dashboard.css';
import '../Style/ServiceProviderForm.css';
import axios from "axios";
import { BASE_URL } from '../Apis/apiConfig';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { Building2, Mail, Phone, MapPin, Briefcase, Globe, Save } from "lucide-react";

const ServiceProviderForm = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    companyEmail: "",
    companyPhone: "",
    companyAddress: "",
    companyServices: "",
    companyWebsite: "",
    societyName: "",
  });

  const [errors, setErrors] = useState({});

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.companyName) newErrors.companyName = "Company name is required";
    if (!formData.companyEmail) newErrors.companyEmail = "Company email is required";
    if (!formData.companyPhone) newErrors.companyPhone = "Company phone is required";
    if (!formData.companyAddress) newErrors.companyAddress = "Company address is required";
    if (!formData.companyServices) newErrors.companyServices = "Company services is required";
    if (!formData.companyWebsite) newErrors.companyWebsite = "Company website is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error when user types
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: null });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const formParams = new URLSearchParams();
    let societyName = localStorage.getItem("society");
    if (societyName === null) {
      societyName = localStorage.getItem("selectedSociety") || sessionStorage.getItem("societyName") || "";
    }
    Object.entries(formData).forEach(([key, value]) => {
      formParams.append(key, value != null ? String(value) : "");
    });

    formParams.append("societyName", societyName != null ? societyName : "");

    try {
      const token = localStorage.getItem("token");

      await axios.post(
        `${BASE_URL}/api/services/add-service-pro`,
        formParams,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
      toast.success("Service Provider added successfully", {
        position: "bottom-right",
        autoClose: 3000,
      });
      navigate("/service-provider");
    } catch (error) {
      console.error("Error adding service provider:", error);
      const message = error.response?.data?.message || "Failed to add service provider";
      toast.error(message, {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="app-container">
      <Header toggleSidebar={toggleSidebar} />
      <ToastContainer />

      <div className="main-layout">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <main className="content-area">
          <SubHeader />

          <div className="premium-form-container">
            <div className="premium-card">
              <div className="card-header-premium">
                <div className="header-content">
                  <h2 className="card-title">Add Service Provider</h2>
                  <p className="card-subtitle">Register a new service provider for your society</p>
                </div>
              </div>

              <form className="premium-form-body" onSubmit={handleSubmit}>
                <div className="form-grid-premium">
                  <div className="form-group-premium">
                    <label className="label-premium">
                      Company Name <span className="required">*</span>
                    </label>
                    <div className="input-wrapper-premium">
                      <Building2 className="input-icon-premium" size={18} />
                      <input
                        type="text"
                        className={`input-premium ${errors.companyName ? 'error' : ''}`}
                        placeholder="e.g. Urban Clap Services"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.companyName && <span className="error-text">{errors.companyName}</span>}
                  </div>

                  <div className="form-group-premium">
                    <label className="label-premium">
                      Email Address <span className="required">*</span>
                    </label>
                    <div className="input-wrapper-premium">
                      <Mail className="input-icon-premium" size={18} />
                      <input
                        type="email"
                        className={`input-premium ${errors.companyEmail ? 'error' : ''}`}
                        placeholder="contact@company.com"
                        name="companyEmail"
                        value={formData.companyEmail}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.companyEmail && <span className="error-text">{errors.companyEmail}</span>}
                  </div>

                  <div className="form-group-premium">
                    <label className="label-premium">
                      Phone Number <span className="required">*</span>
                    </label>
                    <div className="input-wrapper-premium">
                      <Phone className="input-icon-premium" size={18} />
                      <input
                        type="tel"
                        className={`input-premium ${errors.companyPhone ? 'error' : ''}`}
                        placeholder="+91 98765 43210"
                        name="companyPhone"
                        value={formData.companyPhone}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.companyPhone && <span className="error-text">{errors.companyPhone}</span>}
                  </div>

                  <div className="form-group-premium">
                    <label className="label-premium">
                      Website <span className="required">*</span>
                    </label>
                    <div className="input-wrapper-premium">
                      <Globe className="input-icon-premium" size={18} />
                      <input
                        type="text"
                        className={`input-premium ${errors.companyWebsite ? 'error' : ''}`}
                        placeholder="www.company.com"
                        name="companyWebsite"
                        value={formData.companyWebsite}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.companyWebsite && <span className="error-text">{errors.companyWebsite}</span>}
                  </div>

                  <div className="form-group-premium full-width">
                    <label className="label-premium">
                      Address <span className="required">*</span>
                    </label>
                    <div className="input-wrapper-premium">
                      <MapPin className="input-icon-premium" size={18} />
                      <input
                        type="text"
                        className={`input-premium ${errors.companyAddress ? 'error' : ''}`}
                        placeholder="Complete office address"
                        name="companyAddress"
                        value={formData.companyAddress}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.companyAddress && <span className="error-text">{errors.companyAddress}</span>}
                  </div>

                  <div className="form-group-premium full-width">
                    <label className="label-premium">
                      Services Offered <span className="required">*</span>
                    </label>
                    <div className="input-wrapper-premium">
                      <Briefcase className="input-icon-premium" size={18} />
                      <input
                        type="text"
                        className={`input-premium ${errors.companyServices ? 'error' : ''}`}
                        placeholder="e.g. Plumbing, Electrical, Cleaning (comma separated)"
                        name="companyServices"
                        value={formData.companyServices}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.companyServices && <span className="error-text">{errors.companyServices}</span>}
                  </div>
                </div>

                <div className="form-actions-premium">
                  <button type="button" className="btn-secondary-premium" onClick={() => navigate("/service-provider")}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary-premium">
                    <Save size={18} />
                    <span>Save Provider</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ServiceProviderForm;
